/**
 * `useScopeNames` — the breadcrumb resolver seam (redesign B-1/B-2,
 * `ai-docs/lexide-breadcrumb-case-summary-redesign-plan.md` §3.2).
 *
 * Turns an `ActiveSelection` (ids) into the HUMAN names the identity chain
 * renders. It is the seam that lets B2 stop printing `client-mrc6c8xk-5ajbya`
 * in an always-on bar.
 *
 * ## The contract that matters: it NEVER returns an id
 *
 * Not as a fallback, not "temporarily", not "still diagnosable". The standing
 * operator invariant is that no raw entity id reaches lawyer-facing copy, and
 * the whole reason this file exists is that the previous chain treated the id as
 * an acceptable last resort. When nothing human is available the resolver
 * returns the scope's GENERIC label (`Cliente` / `Esfera` / the matter-typed
 * case word) — B-3's ruling, the same shape as the P-GUARD chip:
 * **correct-but-generic beats precise-but-unreadable.**
 *
 * `isGeneric` is reported per segment so a caller can mark it (a `data-*`
 * attribute, a muted tone) without the id ever entering the DOM as text.
 *
 * ## Resolution order, first hit wins (§3.2)
 *
 * 1. `useCaseWorkspaceStore.data.title` — case segment only, and only while a
 *    case workspace is loaded. Freshest: it came from `case.open` on this very
 *    navigation, so it beats a cache written by an earlier selection.
 * 2. the selection's cached display name — written at selection time by the
 *    surface that already held it (nine of thirteen callers). Zero fetches.
 * 3. the React Query result — SAME keys and `staleTime` as `Breadcrumbs.tsx`
 *    (`["client", clientId]`, `["matter", clientId, matterId]`, 60s), so this
 *    shares one cache entry with anything already resolving those entities and
 *    adds no RPC on a warm cache.
 * 4. the generic label.
 *
 * ## ⚠ Why `resolveDisplayNameStrict` and not `resolveDisplayName`
 *
 * The lenient resolver ends `… → name → slug → id → ""`. Calling it here would
 * type-check, pass review, and reinstate the exact leak — and a caller-side
 * `|| genericLabel` would NOT save it, because a returned id is a non-empty
 * string and short-circuits the `||`. See `lib/entity-display.ts`.
 *
 * ## ⚠ Client and matter do not share a shape (§3.2.1 trap 1)
 *
 * `case.context`/on-disk matter is `{id, slug?, displayNames?, matterType?}` —
 * **no `name`** — while client carries `name` AND `displayNames`, and
 * `case.create`'s matter carries `name` but no `displayNames`. The strict
 * resolver handles all three because it tries `displayNames` before `name`;
 * anything reading `.name` directly works for client and silently fails for
 * matter. Only `displayNames.pt` is ever written today — do not build an
 * en/es ladder on top.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchClient, fetchMatter } from "@/lib/lexserver-client";
import { resolveDisplayNameStrict, type DisplayableEntity } from "@/lib/entity-display";
import { useCaseLabel } from "@/lib/case-labels";
import { useLanguageStore } from "@/stores/useLanguageStore";
import { useActiveContextStore, type ActiveSelection } from "@/stores/useActiveContextStore";
import { useCaseWorkspaceStore } from "@/stores/useCaseWorkspaceStore";

/** One resolved segment. `isGeneric` means "no human name existed" — never
 *  "we rendered the id", which cannot happen. */
export interface ScopeName {
  label: string;
  isGeneric: boolean;
}

export interface ScopeNames {
  client: ScopeName | null;
  matter: ScopeName | null;
  case: ScopeName | null;
  /** True while a gap-filling query is in flight. Consumers should keep showing
   *  the generic label rather than a spinner — a chain that changes length or
   *  flickers reads as a bug (§3.2). */
  isResolving: boolean;
}

/**
 * The pure core, extracted so the ordering rules are unit-testable in the
 * project's node env without a React mount or a query client.
 *
 * Every argument is "what that source currently offers, or undefined".
 */
export function resolveScopeName(
  sources: {
    /** Step 1 — case workspace title (case segment only). */
    workspaceTitle?: string;
    /** Step 2 — the selection's cached display name. */
    cached?: string;
    /** Step 3 — the fetched entity, in any of its three shapes. */
    fetched?: DisplayableEntity | null;
  },
  locale: string,
  genericLabel: string,
): ScopeName {
  const nonEmpty = (v: string | undefined) => {
    const t = v?.trim();
    return t ? t : undefined;
  };
  const resolved =
    nonEmpty(sources.workspaceTitle) ??
    nonEmpty(sources.cached) ??
    // STRICT: returns undefined rather than an id. See the header note.
    nonEmpty(resolveDisplayNameStrict(sources.fetched, locale));

  return resolved ? { label: resolved, isGeneric: false } : { label: genericLabel, isGeneric: true };
}

/**
 * Do we actually KNOW this case's matter type?
 *
 * Extracted as a pure predicate because this is where the "Ação Cível on a
 * labor case" defect lived — one layer ABOVE `resolveScopeName`, which had 16
 * tests and could not have caught it. Pure so it can be fenced in a node-env
 * test without mounting anything.
 *
 * False in three distinct situations, all of which must NOT produce a
 * matter-typed word:
 *   - the workspace has not loaded yet (`workspaceCaseId` null/undefined);
 *   - the loaded workspace belongs to a DIFFERENT case — a leftover from the
 *     previous navigation, which would lend this case someone else's type;
 *   - the type is present but BLANK. ⚠ The wire spells "not set" as `""`, so a
 *     truthiness test is required; `?? ` would let the empty string through and
 *     `useCaseLabel("")` falls back to civil. Third sighting of
 *     empty-string-as-absent on this surface (see `case-labels.ts`).
 */
export function matterTypeIsKnownForCase(
  caseId: string | null,
  workspaceCaseId: string | null | undefined,
  matterType: string | undefined,
): boolean {
  if (!caseId) return false;
  if (!workspaceCaseId || workspaceCaseId !== caseId) return false;
  return Boolean(matterType && matterType.trim());
}

/**
 * Resolve the human names for a selection. Pass `null` to get all-null back
 * (no selection = no chain to render).
 */
export function useScopeNames(selection: ActiveSelection | null): ScopeNames {
  const { t } = useTranslation();
  const lang = useLanguageStore((s) => s.lang);
  const workspace = useCaseWorkspaceStore((s) => s.data);
  // The open case id lives on the STORE, not inside `data` — the guard below
  // depends on it, so read it from the right place.
  const workspaceCaseId = useCaseWorkspaceStore((s) => s.caseId);

  const clientId = selection?.clientId ?? null;
  const matterId = selection?.matterId ?? null;
  const caseId = selection?.caseId ?? null;

  // Same keys + staleTime as `Breadcrumbs.tsx` on purpose: one shared cache
  // entry, no extra RPC when something else already resolved these.
  const clientQuery = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => fetchClient(clientId!),
    enabled: Boolean(clientId) && !selection?.clientName,
    staleTime: 60_000,
  });
  const matterQuery = useQuery({
    queryKey: ["matter", clientId, matterId],
    queryFn: () => fetchMatter(clientId!, matterId!),
    enabled: Boolean(clientId && matterId) && !selection?.matterName,
    staleTime: 60_000,
  });

  // The case word is matter-typed ("Reclamação Trabalhista" / "Ação Cível" / …),
  // so the generic case label is not a constant.
  //
  // ⚠ ONLY matter-type it when the type is actually KNOWN, and only from THIS
  // case's workspace. `useCaseLabel(undefined)` falls back to CIVIL by design,
  // so an unguarded read prints "Ação Cível" — a confident, wrong statement
  // about what kind of case a lawyer is looking at — for every tick before the
  // workspace lands. Measured live on a LABOR case by `lexiun-soul`
  // (2026-08-11): the breadcrumb read "Ação Cível" right after creation and
  // again mid-turn, then corrected itself once the title arrived.
  //
  // Two fallbacks were stacking: no title yet ⇒ use the generic word; no
  // matterType yet ⇒ that word is the CIVIL one. Unknown must stay unknown, so
  // until we know the type the neutral scope word is used — exactly what the
  // `client` and `matter` slots below already do with their own tab labels.
  //
  // The `workspaceCaseId === caseId` half is the same guard `workspaceTitle`
  // uses further down, for the same reason: a workspace left over from the
  // PREVIOUS navigation must not lend this case its matter type either.
  // One binding, read twice on purpose: the predicate and the label must be
  // deciding about the SAME value, and two separate reads could drift.
  const workspaceMatterType = workspace?.matterType;
  const typeIsKnown = matterTypeIsKnownForCase(caseId, workspaceCaseId, workspaceMatterType);
  const typedCaseLabel = useCaseLabel(typeIsKnown ? workspaceMatterType : undefined);
  const genericCase = typeIsKnown ? typedCaseLabel : t("contextPane.tabCase");

  if (!selection) {
    return { client: null, matter: null, case: null, isResolving: false };
  }

  // The workspace title only describes the SELECTED case — never a stale one
  // from a previous navigation.
  const workspaceTitle = caseId && workspaceCaseId === caseId ? workspace?.title : undefined;

  return {
    client: clientId
      ? resolveScopeName(
          { cached: selection.clientName, fetched: clientQuery.data },
          lang,
          t("contextPane.tabClient"),
        )
      : null,
    matter: matterId
      ? resolveScopeName(
          { cached: selection.matterName, fetched: matterQuery.data },
          lang,
          t("contextPane.tabMatter"),
        )
      : null,
    case: caseId
      ? resolveScopeName({ workspaceTitle, cached: selection.caseTitle }, lang, genericCase)
      : null,
    isResolving: clientQuery.isFetching || matterQuery.isFetching,
  };
}

/** Convenience: resolve the names for the store's CURRENT selection. */
export function useActiveScopeNames(): ScopeNames {
  const selection = useActiveContextStore((s) => s.selection);
  return useScopeNames(selection);
}
